import FormattedDatePickerReportsV3 from "./FormattedDatePickerReportsV3";
import { useReportExportContext } from "./hooks/useReportExportContext";
import { useSortAlpha } from "./hooks/useSortAlpha";
import { TREAT_TODAY_AS_HELP_DEFAULT } from "./utils/dateUtils";
import {
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  Groups as GroupsIcon,
  Lock as LockIcon,
  PlayArrow as PlayArrowIcon,
  TableChart as TableChartIcon,
} from "@mui/icons-material";
import { Box, Chip, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Button from "components/common/Button";
import Visible from "components/common/Visible";
import * as labels from "config.labels";
import {
  REPORTS_V3_ALLOW_AS_AT,
  REPORTS_V3_ALLOW_IMPORT_EXPORT,
  REPORTS_V3_ALLOW_MULTI_TABLE_REPORTS,
} from "flags";
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const TopNavReportsV3 = ({
  tabs,
  style,
  parentPath,
  navigationState,
  setNavigationState,
  isFullWidth,
  borderless,
  sticky,
  onToggleExpand,
  onDuplicate,
  viewportFill,
  onImport,
  onCreate,
  onCreateMultiTable,
}) => {
  const translateTab = staticDataHooks.useTranslator("tabs");
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const {
    handleExport,
    isExporting,
    handleRun,
    isRunning,
    asAt,
    setAsAt,
    isAsAtInvalid,
    setIsAsAtInvalid,
  } = useReportExportContext() ?? {};
  const { pathname } = useLocation();
  const isOnBuilderPage =
    pathname.endsWith("/edit") || pathname.endsWith("/new");
  const navigate = useNavigate();
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [sortAlpha, handleToggleSort] = useSortAlpha();

  const reportTabs = useMemo(() => {
    const filtered = tabs.filter((t) => !t.rightAlign);
    if (!sortAlpha) return filtered;
    return [...filtered].sort((a, b) => {
      const aLabel = translateTab(`${parentPath}/${a.path}`, {
        default: a.title,
      });
      const bLabel = translateTab(`${parentPath}/${b.path}`, {
        default: b.title,
      });
      return aLabel.localeCompare(bLabel);
    });
  }, [tabs, sortAlpha, translateTab, parentPath]);

  const activeTab = useMemo(
    () =>
      reportTabs.find((tab) => {
        const p = tab.path;
        return (
          pathname === `/${p}` ||
          pathname.endsWith(`/${p}`) ||
          pathname.includes(`/${p}/`)
        );
      }),
    [reportTabs, pathname]
  );

  const handleMenuOpen = (e) => setMenuAnchor(e.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);

  const handleSelectReport = (tabPath) => {
    handleMenuClose();
    if (setNavigationState && parentPath) {
      setNavigationState({ ...navigationState, [parentPath]: tabPath });
    }
    navigate(tabPath);
  };

  const handleEditClick = (e, tabPath) => {
    e.stopPropagation();
    handleMenuClose();
    onToggleExpand?.(tabPath);
  };

  const handleDuplicateClick = (e, tabPath) => {
    e.stopPropagation();
    handleMenuClose();
    onDuplicate?.(tabPath);
  };

  const viewportFillStyle = viewportFill
    ? { position: "sticky", top: 37, zIndex: 100, flex: "0 0 auto" }
    : {};
  const stickyStyle = sticky ? { top: 37 } : {};

  const activeLabel = activeTab
    ? translateTab(`${parentPath}/${activeTab.path}`, {
        default: activeTab.title,
      })
    : translateLabels(labels.REPORTS_NAV_SELECT_REPORT, {
        default: "Select report...",
      });

  return (
    <div
      className={`top-nav no-print ${sticky ? "sticky" : ""} ${
        borderless ? "borderless" : ""
      }`}
      style={{ ...style, ...viewportFillStyle, ...stickyStyle }}
    >
      <div
        className={isFullWidth ? "container full-width" : "container"}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "nowrap",
          paddingTop: 8,
          paddingBottom: 8,
        }}
      >
        <Tooltip
          title={
            sortAlpha
              ? "Sorted alphabetically — click to sort by date"
              : "Sorted by date — click to sort alphabetically"
          }
        >
          <Chip
            label={sortAlpha ? "Alpha sort" : "Date sort"}
            size={"small"}
            color={"primary"}
            onClick={handleToggleSort}
            sx={{
              ml: "12px",
              flexShrink: 0,
              cursor: "pointer",
              fontSize: "0.7rem",
              height: 20,
              width: 70,
            }}
          />
        </Tooltip>
        <Button
          size={"small"}
          endIcon={<ArrowDropDownIcon />}
          onClick={handleMenuOpen}
          sx={{
            minWidth: 200,
            maxWidth: 320,
            justifyContent: "space-between",
            textAlign: "left",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            flexShrink: 0,
          }}
        >
          {activeLabel}
        </Button>
        {activeTab && (
          <>
            {activeTab.multiOutputReport && (
              <Tooltip
                title={translateLabels(labels.REPORTS_NAV_MULTI_TABLE, {
                  default: "Multi-table report",
                })}
                arrow
              >
                <TableChartIcon
                  sx={{
                    fontSize: "1.5rem",
                    color: "text.disabled",
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
            {activeTab.allUsers && (
              <Tooltip
                title={translateLabels(labels.REPORTS_NAV_ALL_USERS, {
                  default: "Shared with all users",
                })}
                arrow
              >
                <GroupsIcon
                  sx={{
                    fontSize: "1.5rem",
                    color: "text.disabled",
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
            {activeTab.locked && (
              <Tooltip
                title={translateLabels(labels.REPORTS_NAV_READ_ONLY, {
                  default: "Read only",
                })}
                arrow
              >
                <LockIcon
                  sx={{
                    fontSize: "1.5rem",
                    color: "text.disabled",
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
            <Tooltip
              title={translateLabels(labels.REPORTS_NAV_EDIT, {
                default: "Edit",
              })}
              arrow
            >
              <IconButton
                size={"small"}
                onClick={(e) => handleEditClick(e, activeTab.path)}
                sx={{
                  color: theme.brand.accent[500],
                  flexShrink: 0,
                  "&:hover": {
                    backgroundColor: `${theme.brand.accent[500]}1A`,
                    color: theme.brand.accent[700],
                  },
                }}
              >
                <EditIcon sx={{ fontSize: "1.5rem" }} />
              </IconButton>
            </Tooltip>
            {handleRun && (
              <Button
                size={"small"}
                startIcon={<PlayArrowIcon />}
                onClick={handleRun}
                isLoading={isRunning}
                isDisabled={isOnBuilderPage}
                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
              >
                {translateLabels(labels.REPORT_RESULTS_RUN_BUTTON, {
                  default: "Run",
                })}
              </Button>
            )}
            {setAsAt && (
              <Visible byTag={REPORTS_V3_ALLOW_AS_AT}>
                <Tooltip
                  // Hidden while the hint under a bad date is showing, so it
                  // does not cover it.
                  title={
                    isAsAtInvalid
                      ? null
                      : translateLabels(labels.REPORTS_V3_TREAT_TODAY_AS_HELP, {
                          default: TREAT_TODAY_AS_HELP_DEFAULT,
                        })
                  }
                  placement={"bottom-start"}
                >
                  <Box sx={{ flexShrink: 0 }} data-testid={"treat-today-as"}>
                    <FormattedDatePickerReportsV3
                      label={translateLabels(
                        labels.REPORTS_V3_TREAT_TODAY_AS_LABEL,
                        { default: "Treat today as" }
                      )}
                      value={asAt || ""}
                      onChangeDate={setAsAt}
                      onInvalidChange={setIsAsAtInvalid}
                      invalidDateHelperText={(example) =>
                        translateLabels(
                          labels.REPORTS_V3_TREAT_TODAY_AS_INVALID,
                          {
                            default: "Enter a date like {example}",
                            replacements: { example },
                          }
                        )
                      }
                      compact
                    />
                  </Box>
                </Tooltip>
              </Visible>
            )}
          </>
        )}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          slotProps={{ paper: { sx: { minWidth: 250 } } }}
        >
          {reportTabs.map((tab) => (
            <MenuItem
              key={tab.path}
              selected={tab.path === activeTab?.path}
              onClick={() => handleSelectReport(tab.path)}
              sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}
            >
              <Box
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {translateTab(`${parentPath}/${tab.path}`, {
                  default: tab.title,
                })}
              </Box>
              {tab.multiOutputReport && (
                <TableChartIcon
                  sx={{
                    fontSize: "0.875rem",
                    color: "text.disabled",
                    flexShrink: 0,
                  }}
                />
              )}
              {tab.allUsers && (
                <GroupsIcon
                  sx={{
                    fontSize: "0.875rem",
                    color: "text.disabled",
                    flexShrink: 0,
                  }}
                />
              )}
              {tab.locked && (
                <LockIcon
                  sx={{
                    fontSize: "0.875rem",
                    color: "text.disabled",
                    flexShrink: 0,
                  }}
                />
              )}
              {onDuplicate && tab.hasDropdown && (
                <Tooltip
                  title={translateLabels(labels.REPORTS_NAV_DUPLICATE, {
                    default: "Duplicate",
                  })}
                  arrow
                >
                  <IconButton
                    size={"small"}
                    onClick={(e) => handleDuplicateClick(e, tab.path)}
                    data-testid={`duplicate-report-${tab.path}`}
                    sx={{
                      color: theme.brand.accent[500],
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor: `${theme.brand.accent[500]}1A`,
                        color: theme.brand.accent[700],
                      },
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip
                title={translateLabels(labels.REPORTS_NAV_EDIT, {
                  default: "Edit",
                })}
                arrow
              >
                <IconButton
                  size={"small"}
                  onClick={(e) => handleEditClick(e, tab.path)}
                  sx={{
                    color: theme.brand.accent[500],
                    flexShrink: 0,
                    "&:hover": {
                      backgroundColor: `${theme.brand.accent[500]}1A`,
                      color: theme.brand.accent[700],
                    },
                  }}
                >
                  <EditIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Tooltip>
            </MenuItem>
          ))}
        </Menu>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            ml: "auto",
            mr: "10px",
          }}
        >
          <Visible byTag={REPORTS_V3_ALLOW_IMPORT_EXPORT}>
            <Button
              size={"small"}
              startIcon={<FileUploadIcon />}
              onClick={onImport}
            >
              {translateLabels(labels.REPORTS_NAV_IMPORT, {
                default: "Import",
              })}
            </Button>
            <Button
              size={"small"}
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              isLoading={isExporting}
            >
              {translateLabels(labels.REPORTS_NAV_EXPORT, {
                default: "Export definition (.json)",
              })}
            </Button>
          </Visible>
          <Button size={"small"} startIcon={<AddIcon />} onClick={onCreate}>
            {translateLabels(labels.REPORTS_NAV_CREATE, { default: "Create" })}
          </Button>
          <Visible byTag={REPORTS_V3_ALLOW_MULTI_TABLE_REPORTS}>
            <Button
              size={"small"}
              startIcon={<TableChartIcon />}
              onClick={onCreateMultiTable}
            >
              {translateLabels(labels.REPORTS_NAV_CREATE_MULTI_TABLE, {
                default: "Create Multi Table",
              })}
            </Button>
          </Visible>
        </Box>
      </div>
    </div>
  );
};

export default TopNavReportsV3;
